import { Component, OnInit } from "@angular/core";
import { Ng4LoadingSpinnerService } from "ng4-loading-spinner";
import { MatDialog } from "@angular/material";
import * as moment from "moment";
import { NotificationsService } from "angular2-notifications";
import { ColDef, GridOptions, GridApi } from "ag-grid-community";
import { UserService } from "../services/user.service";
import { ConfirmationDialogComponent } from "../confirmation-dialog/confirmation-dialog.component";
import { AddGatewayDepositDialog } from "./add-gateway-deposit/add-gateway-deposit.component";
import { amountFormatter } from "../custompipes/amount-formatter";

const adminName = (admin) => (admin ? admin.name || admin.email || "" : "");

const defaults = {
  notif: {
    timeOut: 3000,
    showProgressBar: true,
    pauseOnHover: false,
    clickToClose: true,
    maxLength: 150,
  },
};

// Wallet crediting runs asynchronously in payout_order_processor; reload once more after this delay
const CREDIT_RELOAD_DELAY_MS = 3000;

const STATUS_BADGES = {
  PENDING: "badge-warning",
  DEPOSITED: "badge-success",
  CREDITED: "badge-success",
  FAILED: "badge-danger",
};

const dateTimeFormatter = (params) => (params.value ? moment(params.value).format("MMM DD, YYYY hh:mm:ss A") : "");
const statusRenderer = (params) => (params.value ? `<span class="badge ${STATUS_BADGES[params.value] || "badge-secondary"}">${params.value}</span>` : "");

@Component({
  selector: "app-gateway-deposits",
  templateUrl: "./gateway-deposits.component.html",
  styleUrls: ["./gateway-deposits.component.css"],
})
export class GatewayDepositsComponent implements OnInit {
  _csrf: string;

  private gridApi: GridApi;
  gridOptions: GridOptions;
  columnDefs: ColDef[] = [];
  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 100,
  };

  wallets: any[] = [];
  deposits: any[] = [];
  total: number = 0;
  filter: any = this.getDefaultFilter();
  prev_disabled = true;
  next_disabled = false;
  bankStatuses: string[] = ["PENDING", "DEPOSITED", "FAILED"];
  walletStatuses: string[] = ["PENDING", "CREDITED"];

  myFilter = (d: Date | null): boolean => true;

  constructor(
    private notifSer: NotificationsService,
    private userService: UserService,
    private spinnerService: Ng4LoadingSpinnerService,
    public dialog: MatDialog
  ) {
    this.initializeGrid();
  }

  ngOnInit() {
    this.csrf();
  }

  getDefaultFilter() {
    return {
      page: 1,
      limit: 100,
      fromDate: moment().format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
      walletKey: "ALL",
      bankStatus: "ALL",
      walletStatus: "ALL",
    };
  }

  initializeGrid() {
    this.columnDefs = [
      {
        headerName: "Action",
        field: "action",
        cellRenderer: (params) => {
          const element = params.data;
          const container = document.createElement("div");
          container.style.display = "flex";
          container.style.alignItems = "center";
          if (!element) {
            return container;
          }

          const addBtn = (className: string, title: string, icon: string, onClick: () => void) => {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = `btn btn-sm ${className}`;
            btn.title = title;
            btn.style.marginRight = "5px";
            btn.innerHTML = `<i class="fa ${icon}"></i>`;
            btn.onclick = (e) => {
              e.stopPropagation();
              onClick();
            };
            container.appendChild(btn);
          };
          // DEPOSITED and FAILED are final: only their Reference and Note can still be corrected
          if (element.BankStatus !== "PENDING") {
            addBtn("btn-outline-primary", "Edit Reference / Note", "fa-edit", () => this.openDepositDialog(element));
            return container;
          }
          addBtn("btn-primary", "Edit", "fa-edit", () => this.openDepositDialog(element));
          addBtn("btn-success", "Mark Deposited", "fa-check", () => this.updateBankStatus(element, "DEPOSITED"));
          addBtn("btn-danger", "Mark Failed", "fa-times", () => this.updateBankStatus(element, "FAILED"));

          return container;
        },
        sortable: false,
        filter: false,
        flex: 1,
        minWidth: 110,
      },
      // The date filter is on the deposit (bank transfer) date, so it leads; Entered At is when it was recorded
      {
        headerName: "Deposit Date",
        field: "DepositDate",
        valueFormatter: (params) => (params.value ? moment(params.value).format("MMM DD, YYYY") : ""),
        flex: 1,
      },
      { headerName: "Gateway", field: "GatewayCode", flex: 1 },
      { headerName: "MID", field: "MId", flex: 1 },
      { headerName: "Amount", field: "Amount", flex: 1, valueFormatter: amountFormatter },
      { headerName: "Entered At", field: "createdAt", valueFormatter: dateTimeFormatter, flex: 1.5 },
      { headerName: "Reference", field: "Reference", flex: 1.2 },
      { headerName: "Sender Name", field: "SenderName", flex: 1.2 },
      { headerName: "Note", field: "Note", flex: 1.5 },
      { headerName: "Bank Status", field: "BankStatus", cellRenderer: statusRenderer, flex: 1 },
      { headerName: "Wallet Status", field: "WalletStatus", cellRenderer: statusRenderer, flex: 1 },
      { headerName: "Credited At", field: "CreditedAt", valueFormatter: dateTimeFormatter, flex: 1.5 },
      // Admin refs, populated with name / email by the API
      { headerName: "Created By", field: "CreatedByAdminId", valueGetter: (params) => adminName(params.data && params.data.CreatedByAdminId), flex: 1 },
      { headerName: "Updated By", field: "UpdatedByAdminId", valueGetter: (params) => adminName(params.data && params.data.UpdatedByAdminId), flex: 1 },
    ];

    this.gridOptions = {
      columnDefs: this.columnDefs,
      defaultColDef: this.defaultColDef,
      pagination: false,
      suppressHorizontalScroll: false,
      enableRangeSelection: true,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      animateRows: true,
      rowHeight: 40,
      headerHeight: 40,
    };
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();
  }

  csrf() {
    this.userService.csrf().subscribe(
      (success) => {
        this._csrf = success._csrf;
        this.getWallets();
        this.getData();
      },
      (error) => {
        this.notifSer.error("Error", error.error.message, defaults.notif);
      }
    );
  }

  getWallets() {
    this.userService.getGatewayWallets().subscribe(
      (response: any) => {
        this.wallets = (response && response.data) || [];
      },
      () => {
        this.wallets = [];
      }
    );
  }

  search() {
    this.filter.page = 1;
    this.getData();
  }

  goNext() {
    this.filter.page++;
    this.getData();
  }

  goPrev() {
    if (this.filter.page > 1) {
      this.filter.page--;
      this.getData();
    }
  }

  fromDateFilter(event: any) {
    if (event.value) {
      this.filter["fromDate"] = moment(event.value).format("YYYY-MM-DD");
    }
  }

  toDateFilter(event: any) {
    if (event.value) {
      this.filter["toDate"] = moment(event.value).format("YYYY-MM-DD");
    }
  }

  getData() {
    this.spinnerService.show();
    this.filter._csrf = this._csrf;
    this.userService.getGatewayDeposits(this.filter).subscribe(
      (response: any) => {
        this.deposits = (response && response.data) || [];
        this.total = (response && response.total) || 0;
        if (this.gridApi) {
          this.gridApi.setRowData(this.deposits);
        }
        this.spinnerService.hide();
        this.prev_disabled = this.filter.page <= 1;
        this.next_disabled = this.filter.page * this.filter.limit >= this.total;
      },
      () => {
        this.notifSer.error("Error", "Failed to load gateway deposits", defaults.notif);
        this.spinnerService.hide();
      }
    );
  }

  resetFilters() {
    this.filter = this.getDefaultFilter();
    this.getData();
  }

  // Reload now, and again once the processor has had time to credit the wallet
  reloadAfterDeposited() {
    this.getData();
    setTimeout(() => this.getData(), CREDIT_RELOAD_DELAY_MS);
  }

  openDepositDialog(record?: any) {
    const dialogRef = this.dialog.open(AddGatewayDepositDialog, {
      width: "640px",
      maxWidth: "90vw",
      disableClose: true,
      data: { record: record || {}, wallets: this.wallets, _csrf: this._csrf },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.success) {
        this.notifSer.success("Success", result.message, defaults.notif);
        if (result.deposited) {
          this.reloadAfterDeposited();
        } else {
          this.getData();
        }
      }
    });
  }

  updateBankStatus(record: any, bankStatus: string) {
    const message = bankStatus === "DEPOSITED"
      ? `Mark deposit ${record.Reference} of ${record.Amount} as DEPOSITED? The ${record.WalletKey} wallet will be credited. This is final.`
      : `Mark deposit ${record.Reference} of ${record.Amount} as FAILED? This is final.`;

    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: message,
        buttonText: { ok: "Yes", cancel: "No" },
      },
    }).afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }
      this.spinnerService.show();
      this.userService.updateGatewayDeposit({ _csrf: this._csrf, _id: record._id, BankStatus: bankStatus }).subscribe(
        (response: any) => {
          this.spinnerService.hide();
          this.notifSer.success("Success", (response && response.message) || `Deposit marked ${bankStatus}`, defaults.notif);
          if (bankStatus === "DEPOSITED") {
            this.reloadAfterDeposited();
          } else {
            this.getData();
          }
        },
        (error) => {
          this.spinnerService.hide();
          this.notifSer.error("Error", (error.error && error.error.message) || "Failed to update deposit", defaults.notif);
        }
      );
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/gateway-deposits/gateway-deposits.component.ts